import { useEffect, useState } from 'react';
import { tracksAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Layers } from 'lucide-react';

export const TracksAdminPage = () => {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTrack, setEditingTrack] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    color: '#7B2CBF',
  });

  useEffect(() => {
    loadTracks();
  }, []);

  const loadTracks = async () => {
    try {
      const res = await tracksAPI.getAll();
      setTracks(res.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      color: '#7B2CBF',
    });
    setEditingTrack(null);
  };

  const openEditDialog = (track) => {
    setEditingTrack(track);
    setFormData({
      name: track.name,
      slug: track.slug || '',
      description: track.description || '',
      color: track.color || '#7B2CBF',
    });
    setDialogOpen(true);
  };

  const generateSlug = (name) => {
    return name.toLowerCase()
      .replace(/[àáâãäå]/g, 'a')
      .replace(/[èéêë]/g, 'e')
      .replace(/[ìíîï]/g, 'i')
      .replace(/[òóôõö]/g, 'o')
      .replace(/[ùúûü]/g, 'u')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        slug: formData.slug || generateSlug(formData.name),
      };
      if (editingTrack) {
        await tracksAPI.update(editingTrack.id, data);
        toast.success('Track aggiornata');
      } else {
        await tracksAPI.create(data);
        toast.success('Track creata');
      }
      setDialogOpen(false);
      resetForm();
      loadTracks();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Sei sicuro di voler eliminare questa track?')) return;
    try {
      await tracksAPI.delete(id);
      toast.success('Track eliminata');
      loadTracks();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  return (
    <div className="space-y-6" data-testid="tracks-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mb-2">Tracks</h1>
          <p className="text-muted-foreground">Gestisci le track (Main Stage, Workshop, etc.)</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-primary hover:bg-primary/90" data-testid="create-track-btn">
              <Plus size={18} className="mr-2" />
              Nuova Track
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingTrack ? 'Modifica Track' : 'Nuova Track'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Main Stage"
                  className="bg-white/5 border-white/10"
                  required
                  data-testid="input-name"
                />
              </div>

              <div className="space-y-2">
                <Label>Slug</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="main-stage"
                  className="bg-white/5 border-white/10"
                  data-testid="input-slug"
                />
              </div>

              <div className="space-y-2">
                <Label>Colore</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-14 h-10 p-1 bg-white/5 border-white/10"
                    data-testid="input-color"
                  />
                  <Input
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    placeholder="#7B2CBF"
                    className="bg-white/5 border-white/10 font-mono"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Descrizione</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrizione della track..."
                  className="bg-white/5 border-white/10"
                  data-testid="input-description"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="ghost" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Annulla
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="submit-track-btn">
                  {editingTrack ? 'Aggiorna' : 'Crea'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Table */}
      <div className="glass rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/5 hover:bg-transparent">
              <TableHead>Track</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Descrizione</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tracks.map((track) => (
              <TableRow key={track.id} className="border-white/5" data-testid={`track-row-${track.id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: track.color || '#7B2CBF' }}
                    />
                    <span className="font-medium">{track.name}</span>
                  </div>
                </TableCell>
                <TableCell className="font-mono text-sm text-muted-foreground">{track.slug}</TableCell>
                <TableCell className="text-muted-foreground">{track.description || '-'}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => openEditDialog(track)}
                      data-testid={`edit-track-${track.id}`}
                    >
                      <Pencil size={16} />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleDelete(track.id)}
                      data-testid={`delete-track-${track.id}`}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}

            {tracks.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-12">
                  <Layers className="mx-auto text-muted-foreground mb-4" size={32} />
                  <p className="text-muted-foreground">Nessuna track trovata</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
